import * as THREE from "three";
import { assetUrl } from '../config.js';
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

export class DoorManager {
  constructor(scene, camera, roomSize) {
    this.scene = scene;
    this.camera = camera;
    this.roomSize = roomSize;

    this.doorGroup = null;
    this.doorPanel = null;
    this.sign = null;
    this.signLight = null;
    this.lampGlowDisc = null;

    // Animation state
    this.isHovered = false;
    this.doorRotation = 0;
    this.targetRotation = 0;
    this.doorPivot = null;

    // Track if disposed to prevent async operations after disposal
    this.isDisposed = false;

    this.createDoor();
  }

  async createDoor() {
    // Built against a wall at local -Z, then turned onto the west (-X) wall on the viewer's left.
    this.doorGroup = new THREE.Group();
    this.doorGroup.rotation.y = Math.PI / 2;
    this.doorGroup.userData.wallName = "west";

    const wallZ = -(this.roomSize.width / 2);

    // Load and setup door model
    await this.loadDoorModel(wallZ);

    // Check if disposed during async load
    if (this.isDisposed || !this.doorGroup) return;

    // Create sign above door
    this.createSign(wallZ);

    // Parts loaded after RoomCutaway cut the west wall follow the group's layer.
    const layersMask = this.doorGroup.layers.mask;
    this.doorGroup.traverse((object) => { object.layers.mask = layersMask; });
  }

  addToScene(scene) {
    if (this.doorGroup && scene) {
      scene.add(this.doorGroup);
    }
  }

  async loadDoorModel(wallZ) {
    const loader = new GLTFLoader();

    try {
      const gltf = await loader.loadAsync(assetUrl('models/doors/wooden_door.glb'));

      // Check if disposed during async load
      if (this.isDisposed || !this.doorGroup) return;

      const doorModel = gltf.scene;

      // Store references
      let frameNode = null;
      let doorMesh = null;
      const handleMeshes = [];

      // Configure all meshes, apply materials, and store references in one pass
      doorModel.traverse((child) => {
        if (child.isMesh) {
          // Hide control meshes
          if (child.name.startsWith('CTRL_')) {
            child.visible = false;
            return;
          }

          // Apply materials based on mesh name
          if (child.name === 'Door') {
            // Natural light wood for door panel
            child.material = new THREE.MeshStandardMaterial({
              color: 0xD4A574, // Natural honey oak
              roughness: 0.7,
              metalness: 0.0,
              side: THREE.DoubleSide
            });
            doorMesh = child; // Store for rotation
          } else if (child.name === 'DoorFrame' || child.name === 'DoorFrane') {
            // Matching lighter wood for frame
            child.material = new THREE.MeshStandardMaterial({
              color: 0xE0B886, // Light oak
              roughness: 0.65,
              metalness: 0.0,
              side: THREE.DoubleSide
            });
            // Mark frame to make it static later
            child.userData.isFrame = true;
            frameNode = child;
          } else if (child.name.includes('Handle')) {
            // Brushed silver/chrome handles - add emissive to make them visible in dark areas
            child.material = new THREE.MeshStandardMaterial({
              color: 0xE8E8E8, // Bright silver
              roughness: 0.3,
              metalness: 0.8,
              emissive: 0x404040, // Slight glow to make visible
              emissiveIntensity: 0.2,
              side: THREE.DoubleSide
            });
            handleMeshes.push(child); // Store for rotation
          } else if (child.material?.name === 'Boards - Herringbone') {
            // Lighter wood for herringbone pattern
            child.material = new THREE.MeshStandardMaterial({
              color: 0xE5C29F, // Light pine/maple
              roughness: 0.7,
              metalness: 0.0,
              side: THREE.DoubleSide
            });
          }

          // Enable shadows
          child.castShadow = true;
          child.receiveShadow = true;

          // Tag for click detection
          child.userData.isDoor = true;
        }
      });

      // Get model dimensions
      const box = new THREE.Box3().setFromObject(doorModel);
      const size = box.getSize(new THREE.Vector3());

      // Target door size
      const targetHeight = 2.1;

      // Scale to match target height
      const scale = targetHeight / size.y;
      doorModel.scale.set(scale, scale, scale);

      // Update and recalculate
      doorModel.updateMatrixWorld(true);
      const scaledBox = new THREE.Box3().setFromObject(doorModel);
      const scaledSize = scaledBox.getSize(new THREE.Vector3());

      // Position entire door model at wall
      doorModel.position.set(0, 0, wallZ + scaledSize.z / 2);
      this.doorGroup.add(doorModel);

      // Create black background sized to match the door panel (not the frame)
      if (doorMesh) {
        const wallThickness = 0.3;
        doorMesh.updateMatrixWorld(true);
        const doorBox = new THREE.Box3().setFromObject(doorMesh);
        const doorSize = doorBox.getSize(new THREE.Vector3());

        const backgroundGeometry = new THREE.PlaneGeometry(doorSize.x, doorSize.y);
        const backgroundMaterial = new THREE.MeshBasicMaterial({
          color: 0x000000,
          side: THREE.FrontSide
        });
        const background = new THREE.Mesh(backgroundGeometry, backgroundMaterial);
        // Position on the front face of the wall (inside the room)
        background.position.set(0, doorSize.y / 2, wallZ + (wallThickness / 2) + 0.01);
        // Tag as door so it's included in hover detection
        background.userData.isDoor = true;
        this.doorGroup.add(background);
      }

      // Store references to meshes that should rotate
      this.doorPanel = doorMesh;
      this.handleMeshes = handleMeshes;

    } catch (error) {
      console.error('Error loading door model:', error);
      // Only create fallback if not disposed
      if (!this.isDisposed && this.doorGroup) {
        this.createFallbackDoor(wallZ);
      }
    }
  }

  createFallbackDoor(wallZ) {
    // Safety check
    if (this.isDisposed || !this.doorGroup) return;

    const doorWidth = 0.9;
    const doorHeight = 2.1;
    const doorDepth = 0.05;

    // Create black background
    const backgroundGeometry = new THREE.PlaneGeometry(doorWidth + 0.05, doorHeight + 0.05);
    const backgroundMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      side: THREE.FrontSide
    });
    const background = new THREE.Mesh(backgroundGeometry, backgroundMaterial);
    background.position.set(0, doorHeight / 2, wallZ + 0.01);
    this.doorGroup.add(background);

    // Create pivot at right edge
    this.doorPivot = new THREE.Group();
    this.doorPivot.position.set(doorWidth / 2, 0, wallZ + doorDepth / 2);
    this.doorGroup.add(this.doorPivot);

    // Create simple door panel
    const geometry = new THREE.BoxGeometry(doorWidth, doorHeight, doorDepth);
    const material = new THREE.MeshStandardMaterial({
      color: 0x8B4513,
      roughness: 0.7,
      metalness: 0.1
    });

    this.doorPanel = new THREE.Mesh(geometry, material);
    this.doorPanel.position.set(-doorWidth / 2, doorHeight / 2, 0);
    this.doorPanel.castShadow = true;
    this.doorPanel.receiveShadow = true;
    this.doorPanel.userData.isDoor = true;

    this.doorPivot.add(this.doorPanel);
  }

  createSign(wallZ) {
    // Safety check
    if (this.isDisposed || !this.doorGroup) return;

    const signGroup = new THREE.Group();

    const signWidth = 0.8;
    const signHeight = 0.25;
    const signDepth = 0.03;
    const signY = 2.3; // Lower to be just above 2.1m door
    const wallThickness = 0.3;

    // Sign backing
    const backingGeometry = new THREE.BoxGeometry(signWidth, signHeight, signDepth);
    const backingMaterial = new THREE.MeshStandardMaterial({
      color: 0xF5F5DC,
      roughness: 0.5,
      metalness: 0.0
    });

    const signBacking = new THREE.Mesh(backingGeometry, backingMaterial);
    signBacking.castShadow = true;
    signBacking.receiveShadow = true;
    signGroup.add(signBacking);

    // Create text canvas
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 250;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#F5F5DC';
    ctx.fillRect(0, 0, 800, 250);

    ctx.strokeStyle = '#8B4513';
    ctx.lineWidth = 8;
    ctx.strokeRect(4, 4, 792, 242);

    ctx.fillStyle = '#333333';
    ctx.font = 'bold 90px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Layouts', 400, 125);

    const texture = new THREE.CanvasTexture(canvas);
    const textGeometry = new THREE.PlaneGeometry(signWidth - 0.02, signHeight - 0.02);
    const textMaterial = new THREE.MeshStandardMaterial({
      map: texture,
      roughness: 0.6,
      metalness: 0.0
    });

    const signTextPlane = new THREE.Mesh(textGeometry, textMaterial);
    signTextPlane.position.z = signDepth / 2 + 0.001;
    signGroup.add(signTextPlane);

    // Lamp fixture
    const lampMaterial = new THREE.MeshStandardMaterial({
      color: 0x2C2C2C,
      metalness: 0.8,
      roughness: 0.2
    });

    // Horizontal arm
    const horizontalArmGeometry = new THREE.CylinderGeometry(0.012, 0.012, 0.1, 8);
    const horizontalArm = new THREE.Mesh(horizontalArmGeometry, lampMaterial);
    horizontalArm.rotation.x = Math.PI / 2;
    horizontalArm.position.set(0, signHeight / 2 + 0.15, 0.05);
    signGroup.add(horizontalArm);

    // Lamp shade
    const shadeGeometry = new THREE.ConeGeometry(0.08, 0.12, 16);
    const shadeMaterial = new THREE.MeshStandardMaterial({
      color: 0x1A1A1A,
      metalness: 0.7,
      roughness: 0.3
    });
    const shade = new THREE.Mesh(shadeGeometry, shadeMaterial);

    const lampPosY = signHeight / 2 + 0.21;
    const lampPosZ = 0.1;
    shade.position.set(0, lampPosY, lampPosZ);
    signGroup.add(shade);

    // Glowing disc
    const glowDiscGeometry = new THREE.CircleGeometry(0.075, 16);
    const glowDiscMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFE4B5,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0
    });
    const glowDisc = new THREE.Mesh(glowDiscGeometry, glowDiscMaterial);
    glowDisc.rotation.x = -Math.PI / 2;
    glowDisc.position.set(0, lampPosY - 0.06, lampPosZ);
    signGroup.add(glowDisc);

    this.lampGlowDisc = glowDisc;

    // Spotlight
    this.signLight = new THREE.SpotLight(0xFFF8E7, 0, 3, Math.PI / 3.5, 0.4, 1);
    this.signLight.position.set(0, lampPosY - 0.06, lampPosZ);
    this.signLight.target.position.set(0, 0, 0);
    signGroup.add(this.signLight);
    signGroup.add(this.signLight.target);

    // Position sign on wall (in front of wall, inside room)
    // Wall center is at wallZ, front face is at wallZ + wallThickness/2
    const signZ = wallZ + (wallThickness / 2) + (signDepth / 2);
    signGroup.position.set(0, signY, signZ);

    this.sign = signGroup;
    this.doorGroup.add(this.sign);
  }

  getDoorMeshes() {
    const meshes = [];
    if (this.doorGroup) {
      this.doorGroup.traverse((child) => {
        if (child.isMesh && child.userData.isDoor) {
          meshes.push(child);
        }
      });
    }
    return meshes;
  }

  setHovered(hovered) {
    this.isHovered = hovered;

    if (hovered) {
      // Open door slightly (15 degrees inward)
      this.targetRotation = Math.PI / 12;

      // Turn on light
      if (this.signLight) {
        this.signLight.intensity = 2.5;
      }

      if (this.lampGlowDisc && this.lampGlowDisc.material) {
        this.lampGlowDisc.material.opacity = 0.9;
      }
    } else {
      // Close door
      this.targetRotation = 0;

      // Turn off light
      if (this.signLight) {
        this.signLight.intensity = 0;
      }

      if (this.lampGlowDisc && this.lampGlowDisc.material) {
        this.lampGlowDisc.material.opacity = 0;
      }
    }
  }

  // Returns true if the door moved this frame, so the caller can refresh shadows
  // (shadow maps no longer auto-update every frame — see SceneManager.requestShadowUpdate).
  update(deltaTime) {
    // Smooth door rotation animation
    if (this.doorPanel && Math.abs(this.doorRotation - this.targetRotation) > 0.001) {
      const lerpSpeed = 8.0;
      const t = Math.min(1.0, lerpSpeed * deltaTime);
      this.doorRotation += (this.targetRotation - this.doorRotation) * t;

      // Rotate just the door mesh
      this.doorPanel.rotation.y = this.doorRotation;

      // Rotate handle meshes
      if (this.handleMeshes) {
        this.handleMeshes.forEach(handle => {
          handle.rotation.y = this.doorRotation;
        });
      }
      return true;
    }
    return false;
  }

  setVisible(visible) {
    if (this.doorGroup) {
      this.doorGroup.visible = visible;
    }
  }

  dispose() {
    // Mark as disposed to stop any pending async operations
    this.isDisposed = true;

    if (!this.doorGroup) return;

    if (this.scene) {
      this.scene.remove(this.doorGroup);
    }

    this.doorGroup.traverse((child) => {
      if (child.isMesh || child.isSprite) {
        if (child.geometry) {
          child.geometry.dispose();
        }
        if (child.material) {
          if (child.material.map) {
            child.material.map.dispose();
          }
          child.material.dispose();
        }
      }
    });

    this.doorGroup = null;
    this.doorPanel = null;
    this.sign = null;
  }
}
